import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { tryRefresh } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { LoginPage } from "@/routes/LoginPage";
import { ClientsPage } from "@/routes/ClientsPage";
import { ClientDetailPage } from "@/routes/ClientDetailPage";
import { AuditPage } from "@/routes/AuditPage";
import { DashboardPage } from "@/routes/DashboardPage";
import { AccountDetailPage } from "@/routes/AccountDetailPage";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { LedgerPage } from "@/routes/nexus/LedgerPage";
import { PnlPage } from "@/routes/nexus/PnlPage";
import { ReconciliationPage } from "@/routes/nexus/ReconciliationPage";
import { CrmPage } from "@/routes/nexus/CrmPage";
import { AccountRequestsPage } from "@/routes/nexus/AccountRequestsPage";
import { AccountsPage } from "@/routes/nexus/AccountsPage";
import { AgenciesPage } from "@/routes/nexus/AgenciesPage";
import { RechargeRequestsPage } from "@/routes/nexus/RechargeRequestsPage";
import { WalletsPage } from "@/routes/nexus/WalletsPage";
import { ImportPage } from "@/routes/nexus/ImportPage";
import { ReportingKeysPage } from "@/routes/nexus/ReportingKeysPage";
import { OpsBotPage } from "@/routes/nexus/OpsBotPage";

// Boot-time refresh: attempt one silent token refresh from the HttpOnly refresh
// cookie BEFORE rendering routes, so a returning admin with a valid session
// lands authenticated instead of flashing to /login.
export default function App() {
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    tryRefresh().finally(() => setBooted(true));
  }, []);

  if (!booted) {
    return (
      <div className="grid min-h-screen place-items-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Public auth route */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected app routes */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <ClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <ProtectedRoute>
              <ClientDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <AuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/accounts/:id"
          element={
            <ProtectedRoute>
              <AccountDetailPage />
            </ProtectedRoute>
          }
        />

        {/* NEXOVA admin/ops screens (W7b foundation: placeholders) */}
        <Route
          path="/ledger"
          element={
            <ProtectedRoute>
              <LedgerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pnl"
          element={
            <ProtectedRoute>
              <PnlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reconciliation"
          element={
            <ProtectedRoute>
              <ReconciliationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm"
          element={
            <ProtectedRoute>
              <CrmPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-requests"
          element={
            <ProtectedRoute>
              <AccountRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <AccountsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agencies"
          element={
            <ProtectedRoute>
              <AgenciesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recharge-requests"
          element={
            <ProtectedRoute>
              <RechargeRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallets"
          element={
            <ProtectedRoute>
              <WalletsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ops-bot"
          element={
            <ProtectedRoute>
              <OpsBotPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import"
          element={
            <ProtectedRoute>
              <ImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting-keys"
          element={
            <ProtectedRoute>
              <ReportingKeysPage />
            </ProtectedRoute>
          }
        />

        {/* Redirects — the NEXOVA shell lands on the master ledger. */}
        <Route path="/" element={<Navigate to="/ledger" replace />} />
        <Route path="*" element={<Navigate to="/ledger" replace />} />
      </Routes>
      <Toaster />
    </>
  );
}
